import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import scanner from './images/sacnner.jpg';

interface DynamicField {
  id: number;
  form_type: string;
  field_label: string;
  field_name: string;
  field_type: 'text' | 'email' | 'number' | 'tel' | 'date' | 'textarea' | 'select' | 'file' | 'qr_payment';
  options?: any;
  is_required: number;
  sort_order: number;
}

const Sticky = () => {
  const [isExpressModalOpen, setExpressModalOpen] = useState<boolean>(false);
  const [isVisitorModalOpen, setVisitorModalOpen] = useState<boolean>(false);

  const [visitorFields, setVisitorFields] = useState<DynamicField[]>([]);
  const [expressFields, setExpressFields] = useState<DynamicField[]>([]);
  
  const [visitorFormData, setVisitorFormData] = useState<Record<string, any>>({});
  const [expressFormData, setExpressFormData] = useState<Record<string, any>>({});

  const [submittingVisitor, setSubmittingVisitor] = useState(false);
  const [submittingExpress, setSubmittingExpress] = useState(false);

  const expressModalRef = useRef<HTMLDivElement>(null);
  const visitorModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchFormFields('VISITOR_REGISTRATION');
    fetchFormFields('EXPRESS_INTEREST');
  }, []);

  const fetchFormFields = async (type: 'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST') => {
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields?form_type=${type}`);
      if (res.data.success) {
        if (type === 'VISITOR_REGISTRATION') setVisitorFields(res.data.fields || []);
        else setExpressFields(res.data.fields || []);
      }
    } catch (err) {
      console.error(`Error fetching dynamic fields for ${type}:`, err);
    }
  };

  // Close modals on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expressModalRef.current && !expressModalRef.current.contains(event.target as Node)) {
        setExpressModalOpen(false);
      }
      if (visitorModalRef.current && !visitorModalRef.current.contains(event.target as Node)) {
        setVisitorModalOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVisitorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingVisitor(true);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submit', {
        form_type: 'VISITOR_REGISTRATION',
        data: visitorFormData
      });
      if (res.data.success) {
        alert('🎉 Visitor Registration submitted successfully! Welcome to RMB Jaipur.');
        setVisitorFormData({});
        setVisitorModalOpen(false);
      }
    } catch (err) {
      console.error('Error submitting visitor registration:', err);
      alert('Failed to submit visitor registration.');
    } finally {
      setSubmittingVisitor(false);
    }
  };

  const handleExpressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingExpress(true);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submit', {
        form_type: 'EXPRESS_INTEREST',
        data: expressFormData
      });
      if (res.data.success) {
        alert('🎉 Thank you for Expressing Interest in RMB Jaipur! Our fellowship team will contact you shortly.');
        setExpressFormData({});
        setExpressModalOpen(false);
      }
    } catch (err) {
      console.error('Error submitting express interest:', err);
      alert('Failed to submit express interest.');
    } finally {
      setSubmittingExpress(false);
    }
  };

  const renderDynamicField = (field: DynamicField, formData: Record<string, any>, setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>) => {
    const value = formData[field.field_name] || '';

    const handleChange = (val: any) => {
      setFormData(prev => ({ ...prev, [field.field_name]: val }));
    };

    if (field.field_type === 'qr_payment') {
      let qrImg = scanner;
      let amountStr = '600';

      try {
        if (field.options) {
          const parsed = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
          if (parsed.qr_image) qrImg = parsed.qr_image;
          if (parsed.amount) amountStr = parsed.amount;
        }
      } catch {
        // fallback to default scanner image
      }

      return (
        <div key={field.id} className="mb-4 bg-slate-50 p-3.5 rounded-xl border border-slate-200 shadow-sm">
          <label className="block text-xs font-bold text-slate-800 mb-2">
            {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : null}
          </label>
          <div className="flex flex-col sm:flex-row items-center gap-4 bg-white p-3 rounded-xl border border-slate-200">
            <img src={qrImg} alt="RMB Custom Payment QR Code Scanner" className="w-36 h-36 object-contain rounded-lg border border-slate-300 p-1 shadow-sm bg-white" />
            <div className="text-xs text-slate-600 space-y-1.5 flex-1">
              <p className="font-extrabold text-slate-900">Scan QR Code using Paytm / GPay / PhonePe / UPI</p>
              <p className="text-slate-700 font-bold">
                Registration Amount: <span className="text-emerald-600 font-black text-sm">₹{amountStr}/-</span> per attendee
              </p>
              <p className="text-[11px] text-slate-500">Includes Meeting Entrance & Networking High Tea.</p>
              
              <label className="flex items-center gap-2 mt-2 font-bold text-slate-900 bg-amber-50 p-2 rounded-lg border border-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  required={Boolean(field.is_required)}
                  checked={Boolean(formData[field.field_name])}
                  onChange={(e) => handleChange(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded"
                />
                I have completed the QR payment of ₹{amountStr}/-
              </label>
            </div>
          </div>
        </div>
      );
    }

    if (field.field_type === 'select') {
      let optionsList: string[] = [];
      try {
        if (field.options) optionsList = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
      } catch {
        optionsList = field.options ? (field.options as string).split(',') : [];
      }

      return (
        <div key={field.id} className="mb-3">
          <label className="block text-xs font-bold text-slate-800 mb-1">
            {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : null}
          </label>
          <select
            required={Boolean(field.is_required)}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full border p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] bg-white font-medium"
          >
            <option value="">-- Select {field.field_label} --</option>
            {optionsList.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.field_type === 'textarea') {
      return (
        <div key={field.id} className="mb-3">
          <label className="block text-xs font-bold text-slate-800 mb-1">
            {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : null}
          </label>
          <textarea
            required={Boolean(field.is_required)}
            value={value}
            rows={3}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={`Enter ${field.field_label}`}
            className="w-full border p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] font-medium"
          />
        </div>
      );
    }

    if (field.field_type === 'file') {
      return (
        <div key={field.id} className="mb-3">
          <label className="block text-xs font-bold text-slate-800 mb-1">
            {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : null}
          </label>
          <input
            type="file"
            accept="image/*,.pdf"
            required={Boolean(field.is_required)}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => handleChange(reader.result);
                reader.readAsDataURL(file);
              }
            }}
            className="w-full border p-2 rounded-lg text-xs bg-white"
          />
        </div>
      );
    }

    return (
      <div key={field.id} className="mb-3">
        <label className="block text-xs font-bold text-slate-800 mb-1">
          {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : null}
        </label>
        <input
          type={field.field_type || 'text'}
          required={Boolean(field.is_required)}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`Enter ${field.field_label}`}
          className="w-full border p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] font-medium"
        />
      </div>
    );
  };

  return (
    <div className="!w-screen bg-white p-0 hidden lg:block">
      <div className="w-screen">
        {/* Floating Side Action Buttons */}
        <div className="h-[250px] fixed flex justify-between flex-col -right-20 items-end top-64 z-40">
          <section
            className="w-[193px] h-[36px] text-[15px] font-bold rotate-90 bg-[#02256D] text-[#FDBC12] flex items-center justify-center rounded-t-lg shadow-lg cursor-pointer hover:bg-blue-900 transition"
            onClick={() => setExpressModalOpen(!isExpressModalOpen)}
          >
            Express Interest
          </section>
          <section
            className="w-[193px] h-[36px] text-[15px] font-bold rotate-90 bg-[#E11D48] text-white flex items-center justify-center rounded-t-lg shadow-lg cursor-pointer hover:bg-rose-700 transition"
            onClick={() => setVisitorModalOpen(!isVisitorModalOpen)}
          >
            Visitor Registration
          </section>
        </div>

        {/* Dynamic Express Interest Modal */}
        {isExpressModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200" ref={expressModalRef}>
              <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h2 className="text-lg font-black text-[#02256D]">🤝 Express Interest Form (RMB Fellowship)</h2>
                <button onClick={() => setExpressModalOpen(false)} className="text-slate-400 hover:text-red-500 font-bold text-sm">✕</button>
              </div>

              <form onSubmit={handleExpressSubmit}>
                {expressFields.map(f => renderDynamicField(f, expressFormData, setExpressFormData))}

                <div className="flex justify-end gap-3 pt-3 border-t mt-4">
                  <button
                    type="button"
                    onClick={() => setExpressModalOpen(false)}
                    className="px-4 py-2 border rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingExpress}
                    className="px-6 py-2.5 bg-[#02256D] hover:bg-blue-900 text-white font-bold text-xs rounded-xl shadow transition"
                  >
                    {submittingExpress ? 'Submitting...' : 'Submit Interest'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dynamic Visitor Registration Modal */}
        {isVisitorModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200" ref={visitorModalRef}>
              <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h2 className="text-lg font-black text-[#E11D48]">🎟️ Visitor Meeting Registration Form</h2>
                <button onClick={() => setVisitorModalOpen(false)} className="text-slate-400 hover:text-red-500 font-bold text-sm">✕</button>
              </div>

              <form onSubmit={handleVisitorSubmit}>
                {visitorFields.map(f => renderDynamicField(f, visitorFormData, setVisitorFormData))}

                <div className="flex justify-end gap-3 pt-3 border-t mt-4">
                  <button
                    type="button"
                    onClick={() => setVisitorModalOpen(false)}
                    className="px-4 py-2 border rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingVisitor}
                    className="px-6 py-2.5 bg-[#E11D48] hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow transition"
                  >
                    {submittingVisitor ? 'Submitting...' : 'Confirm Registration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sticky;
